import { env } from '@/config/env';

/**
 * Content dictionary — single source of truth for all user-facing strings.
 *
 * Hand this file to a translation company.
 * Icons, hrefs, and other non-translatable values live in the components.
 */
const KYC_VERIFICATION_COPY = {
  verifyButton: 'Start Verification',
  launchingButton: 'Launching…',
  pendingDescription:
    'Your application is under review. We’ll notify you once verification is complete.',
  errorMessage: 'Verification failed. Please try again or contact support.',
};

const PHONE_NUMBER_PLACEHOLDER = '555 123 4567';
const DID_MONTH_SUFFIX = '/DID/month';

// Shared by the API-authored notification action labels and the client-side fallback labels
// the overview resolves when the API supplies no `actionLabel`.
const VIEW_BILLING_LABEL = 'View billing';
const VIEW_KYC_LABEL = 'View KYC status';
const VIEW_SERVICES_LABEL = 'View services';
const VIEW_FEATURES_LABEL = 'View features';
const VIEW_CARRIAGE_LABEL = 'View carriage';
const VIEW_RECEIPTS_LABEL = 'View receipts';
const VIEW_TEAM_LABEL = 'View team';
const TOP_UP_CREDIT_LABEL = 'Top up credit';

export const DICTIONARY = {
  // ─── Common ───────────────────────────────────────────────────────────────
  common: {
    noResults: 'No results found',
    // Announced by the navigation progress bar and reusable by any other busy state.
    loading: 'Loading…',
    comingSoon: 'Coming soon',
    cancel: 'Cancel',
    saveChanges: 'Save changes',
    saving: 'Saving…',
    done: 'Done',
    copy: 'Copy',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    previous: 'Previous',
    next: 'Next',
    markAsRead: 'Mark as read',
    back: 'Back',
    continue: 'Continue',
    emailLabel: 'Email address',
    // Rendered wherever we send someone to their inbox. Activation mail is transactional and
    // routinely filtered, so every one of those screens has to say this — keep it here rather
    // than folding it into each message, or the wording drifts apart.
    checkJunkFolder: 'Not there? Check your junk or spam folder.',
    // Shown when the backend's rate limiter refuses a redeem or a resend. Deliberately says
    // nothing about the code itself — the attempt never reached the check.
    tooManyAttempts: 'Too many attempts. Wait a minute, then try again.',
    didUnit: '/DID',
    didSuffix: DID_MONTH_SUFFIX,
    infoIcon: 'ⓘ',
    tellMeMore: 'Tell me more',
    openAsset: 'Open asset',
    starterCredit: 'Starter credit',
    phoneNumberPlaceholder: PHONE_NUMBER_PLACEHOLDER,
    emptyValue: '—',
    currencySymbol: '$',
    // Shared by every 6-digit code input — authenticator codes and the emailed activation code.
    sixDigitCodePlaceholder: '1 2 3 4 5 6',
    // Keyed by the canonical `RoleType` vocabulary — resolve raw backend role names
    // through `getRoleLabel` (src/config/role-labels.ts) rather than indexing directly.
    roleLabels: {
      globalAdmin: 'Global Admin',
      admin: 'Admin',
      billingAdmin: 'Billing Admin',
    },
    toast: {
      saveSuccess: 'Changes saved',
      saveError: 'Failed to save. Please try again.',
      loadError: 'Failed to load data',
    },
    validation: {
      emailInvalid: 'Please enter a valid email address.',
      fullNameRequired: 'Full name is required.',
      firstNameRequired: 'First name is required.',
      lastNameRequired: 'Last name is required.',
      roleRequired: 'Please select your role.',
      codeRequired: 'Please enter the 6-digit code.',
      phoneRequired: 'Phone number is required.',
      phoneInvalid: 'Enter a phone number between 4 and 15 digits.',
      amountRequired: 'Enter an amount.',
      amountPositive: 'Enter an amount greater than 0.',
      amountNotNegative: 'Must be 0 or greater.',
      amountBelowMin: (min: string) => `Minimum top-up is ${min}.`,
      amountAboveMax: (max: string) => `Maximum top-up is ${max}.`,
    },
  },

  // ─── Brand ────────────────────────────────────────────────────────────────
  brand: {
    prefix: 'ping',
    suffix: 'co',
    full: 'PingCo',
    version: env.VITE_APP_VERSION,
  },

  // ─── Nav ──────────────────────────────────────────────────────────────────
  nav: {
    links: [
      { label: 'Features', href: '#features' },
      { label: 'How it works', href: '#how-it-works' },
    ],
    themeToggleAriaLabel: 'Toggle colour mode',
    lightModeTooltip: 'Switch to dark mode',
    darkModeTooltip: 'Switch to light mode',
    userMenuTooltip: 'User console',
  },

  // ─── Footer ───────────────────────────────────────────────────────────────
  footer: {
    copyright: (year: number) => `\u00a9 ${String(year)} PingCo. All rights reserved.`,
  },

  // ─── Auth ─────────────────────────────────────────────────────────────────
  auth: {
    signIn: {
      idle: 'Sign In',
      loading: 'Signing in\u2026',
    },
    signOut: {
      idle: 'Sign Out',
      loading: 'Signing out\u2026',
    },
    userMenu: {
      consoleHeading: 'Console',
      tcapPortalLabel: 'TCAP Portal',
      tcapDemosLabel: 'TCAP Demos',
      notificationsLabel: 'Notifications',
      signOut: 'Sign out',
      pickerTitle: 'Select a demo user',
    },
    signInCard: {
      tab: 'Sign in',
      tabSubtitle: 'Existing account',
      heading: 'Welcome back',
      subheading: 'Sign in to manage your wholesaler workspace.',
      workEmail: {
        label: 'Work email',
        placeholder: 'demo@pingco.cloud',
      },
      code: {
        label: 'Signin Code',
        error: 'Enter the 6-digit code we emailed you.',
      },
      continueButton: 'Continue \u2192',
      orDivider: 'OR',
      mssoButton: 'Sign in with Microsoft 365',
      newAccount: 'New to PingCo?',
      createAccountLink: 'Create an account',
      notImplemented: 'Email/password sign-in not yet implemented. Please use Microsoft 365.',
      signInError: 'Sign-in failed. Please try again.',
      insideWorkspace: {
        heading: 'INSIDE YOUR WORKSPACE',
        regions: {
          title: 'Configure regions & services',
          body: 'Pick countries, choose carriage and tailor your service catalogue.',
        },
        billing: {
          title: 'Prepaid, transparent billing',
          body: 'Pay as you go with live cost estimates and no surprises.',
        },
        goLive: {
          title: 'Go live in minutes',
          body: 'Brand your portal, activate carriage and start routing instantly.',
        },
      },
    },
    // Copy for the two-step emailed-code sign-in, shared by the landing card and the /signin
    // route. Field labels are reused from `signInCard` above — same inputs, same words.
    // Named signInFlow, not signIn — `auth.signIn` above is the sign-in *button* label, and the
    // page badge reuses it rather than repeating the same words under a second key.
    signInFlow: {
      pageTitle: 'Sign in to your workspace',
      subtitle: 'We email you a one-time code — no password, no authenticator app.',
      emailStep: {
        submitButton: 'Email me a code \u2192',
        // Dev-only shortcut, never rendered in a build: `/auth/login` 404s unless the backend runs
        // in Development, and the button is gated on `isDev` on top of that. Labelled "dev" so a
        // screenshot from a dev machine is never mistaken for the real sign-in screen.
        devSignInButton: 'Dev sign-in (skip the code)',
      },
      codeStep: {
        heading: 'Enter your sign-in code',
        // Deliberately says nothing about whether the address has an account: the backend answers
        // the same way either way, and copy that implied otherwise would turn this endpoint into a
        // way of testing who our customers are.
        neutralResult: "If that address has an account, we've emailed it a sign-in code.",
        expiryNote: 'The code expires 15 minutes after it was sent.',
        submitButton: 'Sign in \u2192',
        resendPrompt: 'Code expired, or never arrived?',
        changeEmail: 'Use a different email',
      },
      // The emailed link redeems itself on arrival and lands on the dashboard, so the only copy
      // left here is the panel shown when the link no longer works.
      hotlink: {
        expired: {
          heading: 'This sign-in link is no longer valid',
          body: 'Sign-in links last 15 minutes and work once. Request a new code to carry on.',
          button: 'Request a new code',
        },
      },
      errors: {
        // 429 from the backend's issuance throttle — 3 codes per 15 minutes per address.
        throttled: 'Too many sign-in codes requested. Wait a few minutes, then try again.',
        requestFailed: 'We could not send a code just now. Please try again.',
      },
    },
    register: {
      tab: 'Register',
      tabSubtitle: 'Create Account',
      heading: 'Create your account',
      subheading: 'A few details to get your workspace ready.',
      companyName: {
        label: 'Company name',
        placeholder: 'e.g. PingCo Telecom',
      },
      headquartersCountry: {
        label: 'Headquarters country',
      },
      firstName: {
        label: 'First name',
        placeholder: 'Jane',
      },
      lastName: {
        label: 'Last name',
        placeholder: 'Doe',
      },
      workEmail: {
        label: 'Work email',
        placeholder: 'jane.doe@acmetelecom.com',
      },
      phoneNumber: {
        label: 'Phone number',
        placeholder: PHONE_NUMBER_PLACEHOLDER,
      },
      role: {
        label: 'Your role',
        placeholder: 'Select your role',
      },
      businessType: {
        label: 'Business type',
        placeholder: 'Select business type',
      },
      partner: {
        label: 'Partner',
        readOnlyBadge: 'READ ONLY',
        defaultName: 'PingCo',
        helperText: (name: string) => `You're registering under the ${name} partner brand.`,
        companyDetailsNote:
          "We'll capture your company details (name, country, business type) when you set up your workspace.",
      },
      createButton: 'Create account \u2192',
      postSubmitMessage:
        "You'll set up two-factor authentication after activating from your inbox.",
      errors: {
        registrationFailed: 'Registration failed. Please try again.',
        duplicateEmail: 'An account with that email already exists.',
      },
    },
    checkEmail: {
      heading: 'Check your inbox',
      subheading: (email: string) => `We sent an activation link to ${email}`,
      resend: 'Resend activation email',
      resendCooldown: (s: number) => `Resend available in ${String(s)}s`,
      resendSuccess: 'Email resent successfully',
    },
    recoverAccount: {
      badge: 'Account Recovery',
      pageTitle: 'Recover your account',
      subtitle: 'Lost your activation link, or let it expire? We will send you a new one.',
      trigger: {
        prompt: 'Cannot sign in?',
        button: 'Recover account',
      },
      emailStep: {
        heading: 'What address did you register with?',
        body: 'We will email you a fresh activation link. Use this if your original link expired or never arrived.',
        submitButton: 'Send a new link',
        // Identical whether or not the address has an account — a difference would reveal which
        // addresses are registered.
        neutralResult:
          "If an account exists for that address, we've sent a new activation email. Open the link in it to finish activating.",
      },
    },
    notFound: {
      heading: 'This link has expired',
      message: 'Activation links expire after 1 hour or may have already been used.',
      requestNew: 'Request a new link',
      resendError: 'Could not send a new link. Please try again.',
    },
    pendingActivation: {
      heading: 'Check your inbox',
      subheading: 'Your account is not yet activated.',
      body: "We've sent you an activation email with a link. Open it to set your password and two-factor authentication before signing in.",
      expiredPrompt: 'Activation link or code expired?',
      emailPrompt: 'Enter the address you registered with and we’ll send a new activation email.',
      // Deliberately identical whether or not the address has an account — a difference here would
      // tell an attacker which addresses are registered.
      neutralResult:
        "If an account exists for that address, we've sent a new activation email to it.",
      backToSignIn: '← Back to sign in',
    },
  },

  // ─── Account Activation ───────────────────────────────────────────────────
  activation: {
    badge: 'Account Activation',
    pageTitle: 'Welcome to PingCo, {name}',
    subtitle: 'One click to activate your account and sign in.',
    stepLabels: {
      activate: 'Activate',
      done: 'Done',
    },
    // The hotlink panel: the activation link carried a token, it has been peeked, and one click
    // spends it. Mirrors auth.signInFlow.hotlink — same reason for the click, different credential.
    activate: {
      heading: 'Activate your account',
      intro: 'You opened an activation link for {email}.',
      rationale: 'One click confirms it was you, not your mail provider, that opened this link.',
      button: 'Activate my account \u2192',
    },
    // The fallback for reading mail somewhere other than where you want to act: the 6-digit code
    // from the same email, typed alongside the address it was sent to.
    codeEntry: {
      heading: 'Enter your activation code',
      intro: 'Enter the email address you were invited on and the 6-digit code we sent it.',
      codeLabel: 'Activation code',
      continueButton: 'Activate and continue →',
      expiryNote: 'The code expires 1 hour after it was sent.',
      resendPrompt: 'Code expired, or never arrived?',
      newEmailSent: {
        heading: 'A new activation email is on its way',
        // A resent code is no longer tied to the link it arrived with — the backend matches the
        // newest live credential for the address — so it can be typed straight in here.
        body: 'Enter the code from the newest email — the previous one no longer works.',
      },
      errors: {
        invalidCode: 'That code is incorrect or has expired. Check your email and try again.',
      },
    },
    // DEFERRED, not removed - authenticator enrollment is postponed, not dropped. Restoring it
    // also restores stepLabels.verifyEmail ('Verify Email') and stepLabels.setupAuthenticator
    // ('Setup Authenticator') above - the two-step labels this one-step flow replaced - and
    // success.goToSignInButton ('Go to sign in') below, since enrollment ended at the sign-in
    // page rather than signed in. Uncomment when MFA returns.
    // mfa: {
    //   heading: 'Set up your authenticator app',
    //   intro:
    //     'Verified {email}. Scan this code with an authenticator app, then enter the 6-digit code it shows to confirm.',
    //   appSuggestions: {
    //     label: 'Recommended apps',
    //     items: ['Microsoft Authenticator', 'Google Authenticator', 'Authy', '1Password'],
    //   },
    //   qrCodeAlt: 'QR code for authenticator app setup',
    //   orEnterManually: 'Or copy and paste this secret manually:',
    //   codeLabel: '6-digit code',
    //   confirmButton: 'Confirm and finish \u2192',
    //   errors: {
    //     invalidCode: 'Invalid code. Please try again.',
    //   },
    // },
    success: {
      heading: 'Account activated',
      message: "You're signed in. Let's get your workspace configured.",
      goToWorkspaceButton: 'Go to my workspace \u2192',
    },
  },

  // ─── Hero ─────────────────────────────────────────────────────────────────
  hero: {
    badge: 'Voice Enablement for Service Providers',
    heading: {
      main: 'Accelerate your sales',
      accent: 'with full automation.',
    },
    body: 'Sign up, add credit, and go live \u2014 without speaking to anyone. Enterprise-grade voice and communications with the simplicity you expect.',
    cta: {
      primary: 'Free Trial',
      secondary: 'Learn more',
      secondaryHref: '#how-it-works',
    },
    trust: [
      { label: 'Instant activation' },
      { label: 'No approval gates' },
      { label: 'Global coverage' },
    ],
  },

  // ─── Features section ─────────────────────────────────────────────────────
  features: {
    eyebrow: 'Let automation do the work for you',
    heading: 'Everything you need to get started',
    subheading:
      'A frictionless onboarding experience designed for service providers who want to move fast.',
    items: [
      {
        title: 'Live in minutes',
        description:
          'Sign up, configure services, add credit, and start routing calls \u2014 all without waiting for approvals.',
      },
      {
        title: 'Prepaid credit model',
        description:
          'Top up your balance and draw down as you use. No invoices, no surprises. Full control over your spend.',
      },
      {
        title: 'Progressive trust',
        description:
          'Start with safe defaults. Complete KYC at your pace to unlock higher limits and more capabilities.',
      },
      {
        title: 'Multi-user console',
        description:
          'Invite team members, manage roles, and maintain full visibility across your organization.',
      },
      {
        title: 'Global reach',
        description:
          'Enable services across multiple regions. BYOC or use PingCo carriage \u2014 your choice.',
      },
      {
        title: 'Service flexibility',
        description:
          'Operator Connect, Zoom, Webex and more. Enable what you need, when you need it.',
      },
    ],
  },

  // ─── CTA section ──────────────────────────────────────────────────────────
  cta: {
    heading: 'Ready to accelerate?',
    body: 'Join thousands of service providers growing their revenue with the Wholesaler Activation Service Platform (WASP).',
    exploreFeatures: 'Explore features →',
    exploreFeaturesHref: '#features',
  },

  // ─── Setup wizard ─────────────────────────────────────────────────────────
  setup: {
    sidebar: {
      title: 'Get started',
      sections: [
        {
          label: 'SIGN UP',
          steps: ['Personal Details', 'Company'],
        },
        {
          label: 'SET UP',
          steps: ['Regions & Carriage', 'Countries', 'Services', 'Features'],
        },
        {
          label: 'TOP UP',
          steps: ['Credit'],
        },
        {
          label: 'SCALE UP',
          steps: ['Branding', 'Activate'],
        },
      ],
    },
    header: {
      badge: 'Voice Enablement for Service Providers',
      heading: {
        main: 'Accelerate your sales',
        accent: 'with full automation.',
      },
      body: 'Sign up, add credit, and go live \u2014 without speaking to anyone.',
    },
    nav: {
      back: '\u2190 Back',
      continue: 'Continue \u2192',
      activateAccount: 'Activate account \u2192',
    },
    personalDetails: {
      mssoButton: 'Sign up with Microsoft 365',
      mssoNote:
        'Only custom business domains \u2014 .onmicrosoft.com and personal accounts are blocked.',
      orDivider: 'or sign up with email',
      fullName: {
        label: 'Full name',
        placeholder: 'Jane Smith',
      },
      businessEmail: {
        label: 'Business email',
        placeholder: 'jane@acmetelecom.com',
        helperText: 'Consumer email domains (Gmail, Outlook, etc.) are not accepted.',
        errorConsumerDomain:
          'Consumer email domains are not accepted. Please use your business email.',
      },
      role: {
        label: 'Your role',
        placeholder: 'Select your role',
      },
      infoPanel: {
        title: 'Secure Your Identity',
        description:
          'We verify your identity to protect your account and ensure only authorised users access your services.',
        bullets: [
          'Business email prevents fraud & abuse',
          'SSO streamlines onboarding for your team',
          'You’ll set up two-factor authentication after activating from your inbox',
        ],
      },
    },
    company: {
      intro: 'Tell us about your company.',
      name: {
        label: 'Company name',
        placeholder: 'e.g. PingCo Telecom',
        errorRequired: 'Company name is required.',
      },
      businessType: {
        label: 'What best describes your core business?',
        placeholder: 'Select business type',
        errorRequired: 'Please select your business type.',
      },
      country: {
        label: 'Country',
        placeholder: 'Select country',
        errorRequired: 'Please select your country.',
      },
      infoPanel: {
        title: 'Tailor Your Experience',
        description:
          'Company details help us customise pricing, compliance, and support for your region and industry.',
        bullets: [
          'Localised pricing & tax handling',
          'Industry-specific feature recommendations',
          'Regulatory compliance by jurisdiction',
        ],
      },
    },
    regionsCarriage: {
      intro: 'Select your regions and carriage preference.',
      regions: { label: 'Regions', global: 'Global \u2014 All regions' },
      carriage: {
        label: 'Carriage preference',
        helper: "Select one or both \u2014 you'll assign per country next.",
        byoc: 'Bring Your Own Carrier',
        pingco: 'PingCo Carriage',
      },
      infoPanel: {
        title: 'Regions & Carriage',
        description:
          'Select where you operate and how voice traffic will be carried in those regions.',
        bullets: [
          'Lower latency with local PoPs',
          'BYOC or PingCo managed carriage',
          'Regional number availability',
        ],
      },
    },
    countries: {
      intro:
        'Configure carriage for each country in your selected regions. You can select both BYOC(Coming Soon) and PingCo per country.',
      searchPlaceholder: 'Search countries across all regions...',
      infoPanel: {
        title: 'Country Configuration',
        description:
          'Fine-tune carriage preferences per country. You can assign BYOC, PingCo, or both to each country.',
        bullets: [
          'Per-country carriage control',
          'PingCo available in 30+ countries',
          'Mix carriers across your footprint',
        ],
      },
    },
    tellMeMore: {
      noAsset: 'No walkthrough has been attached to this one yet.',
      noLongDescription: 'No further detail has been published for this one yet.',
    },
    services: {
      intro: 'Configure service visibility for your platform.',
      learnMore: 'Learn more',
      infoPanel: {
        title: 'Build Your Stack',
        description:
          'Choose the services that match your platform so we can pre-configure integrations and documentation.',
        bullets: [
          'Pre-built integrations for each platform',
          'Guided setup & API references',
          'Enable only what you need',
        ],
      },
    },
    features: {
      intro: 'Configure platform features.',
      infoPanel: {
        title: 'Cost Per DID',
        description: 'Monthly cost breakdown based on your selected features.',
        baseDid: 'Active Platform DID',
        baseDidPrice: '$1.80',
        totalLabel: 'Total / DID / month',
        currency: 'All prices in USD.',
      },
    },
    credit: {
      starterCredit: {
        amount: (amount: string) => `${amount} USD`,
        description: (amount: string) =>
          `Every new account receives ${amount} USD credit to get started — no top-up required.`,
      },
      paymentDetails: {
        label: 'Payment details',
        poweredBy: 'Powered by Stripe',
        nameOnCard: 'Name on card',
        nameOnCardPlaceholder: 'J Smith',
        devTestCardHint:
          'Dev mode test card: 4242 4242 4242 4242, any future expiry date, any 3-digit CVC, any postal code.',
      },
      cardOnFile: {
        label: (brand: string, last4: string) => `${brand} •••• ${last4}`,
        changeLabel: 'Change card',
      },
      cardAfterActivation: {
        title: 'Add your card after activation',
        description:
          'Card details are collected once your wholesaler account exists. Choose your starting credit now, then add a card from Credit & Billing to top up.',
      },
      whatYouGet: {
        title: (amount: string) => `What ${amount} gets you`,
        example: 'Example: 5 DIDs · PingCo (United Kingdom)',
        // Authored worked example — the costs a five-number platform carries in a month.
        items: [
          { label: '5× TCAP Platform Fee', value: '$27.00' },
          { label: '5× Telephone Numbers from PingCo', value: '$10.00' },
        ],
        fixedCosts: 'Fixed costs',
        remaining: 'Remaining',
        domesticMins: '≈ domestic mins',
      },
      infoPanel: {
        title: 'Cost Per DID',
        description: 'Monthly cost breakdown based on your selected features.',
        baseDid: 'Active Platform DID',
        baseDidPrice: '$1.80',
        totalLabel: 'Total / DID / month',
        currency: 'All prices in USD.',
      },
      errors: {
        cardError: 'Card error. Please try again.',
        stripeProxyError:
          'Unable to create Stripe setup intent. Please start the local Stripe proxy.',
      },
      devSkip: 'Skip payment (dev only)',
    },
    branding: {
      intro: 'Choose your portal hostname and upload your logo.',
      hostname: {
        label: 'Portal hostname',
        placeholder: 'northwind-telecom',
        suffix: '.tcap.cloud',
        helperText: 'This hostname will be used for your provisioned TCAP portal.',
        available: (hostname: string) => `${hostname}.tcap.cloud is available`,
        errorRequired: 'Portal hostname is required.',
      },
      logo: {
        label: 'Logo',
        dropLabel: 'PNG only',
        // chooseFile: 'Choose file',
        removeFile: 'Remove',
        errorTooLarge: 'Logo must be 5 MB or smaller.',
        errorUnsupportedType: 'Logo must be a PNG, JPG, SVG or WebP image.',
        toast: {
          uploadError: 'Your workspace logo could not be uploaded',
        },
      },
      note: {
        text: 'Further branding options and a fully custom domain (e.g. portal.yourcompany.com) can be configured from the TCAP portal once your service has been provisioned.',
      },
      infoPanel: {
        title: 'Pay As You Go',
        description:
          'Prepaid credit means no surprise invoices. Top up anytime and track usage in real time.',
        bullets: [
          'No contracts or commitments',
          'Real-time usage dashboards',
          'Low-balance alerts & auto-top-up',
        ],
      },
    },
    activate: {
      orderSummary: 'Order Summary',
      reviewIntro: 'Review your configuration for',
      sections: {
        account: 'ACCOUNT',
        regionsAndCoverage: 'REGIONS & COVERAGE',
        services: 'SERVICES',
        costPerDid: 'COST PER DID / MONTH',
        features: 'FEATURES',
        credit: 'CREDIT',
        stripeChargeBreakdown: 'SERVICE CHARGE BREAKDOWN',
        branding: 'BRANDING',
      },
      fields: {
        name: 'Name',
        email: 'Email',
        company: 'Company',
        role: 'Role',
        type: 'Type',
        regions: 'Regions',
        carriage: 'Carriage',
        countries: 'Countries',
        configured: 'configured',
        freeCredit: 'Free credit',
        additionalCredit: 'Additional credit',
        cardVerificationKyc: 'Card verification (KYC)',
        totalDid: 'Total / DID / month',
        totalCredit: 'Total credit',
        chargeNow: 'Charge now',
        payment: 'Payment',
        portal: 'Portal',
        stripePaymentIntent: 'Payment intent',
        stripeStatus: 'Charge status',
        stripeChargeAmount: 'Charged amount',
        stripeCard: 'Card',
        stripeTax: 'Tax',
        stripeFeeTotal: 'Service fee total',
        stripeNet: 'Net after fees',
        stripeExchangeRate: 'Exchange rate',
        stripeLocalCharge: 'Est. charge in card currency',
        stripeFeeDetail: 'Fee detail',
        stripeEstimatePhase: 'Estimated breakdown',
        stripeExactPhase: 'Exact breakdown',
      },
      values: {
        savedCardViaStripe: 'Saved card',
        noCardOnFile: 'No card on file',
        notAvailable: '—',
        pending: 'Pending',
        availableAfterActivation: 'Available after activation starts',
        calculatedAtChargeTime: 'Calculated when charge is created',
        estimatedTag: '(Estimated)',
        exactTag: '(Exact)',
        beforeCharge: 'Before charge',
        afterCharge: 'After charge',
        safeDefaultsNote:
          "You'll start with safe defaults. Complete KYC later to unlock higher limits.",
      },
      termsAndConditions: {
        checkboxLabel: 'I have read and agree to the',
        linkText: 'Terms and Conditions',
        linkSuffix: '— please open and read before accepting.',
        dialogTitle: 'Terms and Conditions',
        dialogSubtitle: 'Please scroll to the bottom to acknowledge.',
        closeAriaLabel: 'Close terms dialog',
        scrollNote: 'Scroll to the bottom to enable acceptance.',
        checkboxDisabledHint:
          'Open and read the full Terms and Conditions to the bottom before you can accept them.',
        printButton: 'Print',
        acceptButton: 'I Accept',
        definitionsTitle: '1. Definitions and Interpretation',
        definitionsHeaders: { term: 'Term', definition: 'Definition' },
        definitions: [
          {
            term: 'Auto Top-Up',
            definition:
              "means the optional functionality allowing PingCo to automatically recharge the Customer's Prepaid Credit balance using a nominated Payment Method.",
          },
          { term: 'Customer', definition: 'means the individual or entity accepting these Terms.' },
          {
            term: 'Fees',
            definition:
              'means the fees, charges and pricing applicable to the Services as published within the Portal from time to time.',
          },
          {
            term: 'Payment Method',
            definition:
              'means a valid credit card, debit card or other payment mechanism accepted by PingCo.',
          },
          {
            term: 'Portal',
            definition:
              'means TCAP, or any successor portal or interface made available by PingCo through which the Customer may acquire, provision, manage or consume Services.',
          },
          {
            term: 'Prepaid Credit',
            definition: 'means funds loaded by the Customer for future consumption of Services.',
          },
          {
            term: 'Services',
            definition:
              'means any products, services, functionality, features or capabilities made available by PingCo through the Portal from time to time.',
          },
          {
            term: 'TCAP (TelCo Automation Platform)',
            definition:
              "means PingCo's proprietary automation platform used for the provisioning, management, routing, billing, administration and automation of telecommunications and related services.",
          },
          {
            term: 'Third Party Service',
            definition:
              'means a product or service supplied by a Third Party Vendor and made available in connection with the Services.',
          },
          {
            term: 'Third Party Vendor',
            definition:
              'means a carrier, cloud provider, software vendor, infrastructure provider or other third party supplier used in connection with the Services.',
          },
          {
            term: 'User',
            definition:
              'means any person authorised by the Customer to access or use the Services.',
          },
        ],
        sections: [
          {
            number: 2,
            title: 'Acceptance',
            clauses: [
              {
                number: '2.1',
                text: 'These Terms govern access to and use of the Services made available through the Portal.',
              },
              {
                number: '2.2',
                text: 'By creating an account, loading Prepaid Credit, placing an Order through the Portal, enabling a Service, or otherwise accessing or using the Services, the Customer agrees to be bound by these Terms.',
              },
              {
                number: '2.3',
                text: 'These Terms form a binding agreement between PingCo and the Customer.',
              },
            ],
          },
          {
            number: 3,
            title: 'Services',
            clauses: [
              {
                number: '3.1',
                text: 'PingCo may make Services available through the Portal from time to time.',
              },
              {
                number: '3.2',
                text: 'The Services available, applicable Fees, supported countries, features and functionality may change from time to time.',
              },
              {
                number: '3.3',
                text: 'Certain Services may be subject to additional service-specific terms, policies, Vendor Terms or technical requirements published within the Portal.',
              },
              {
                number: '3.4',
                text: 'PingCo may add, remove, modify, replace or discontinue Services at any time.',
              },
            ],
          },
          {
            number: 4,
            title: 'Provision of Services',
            clauses: [
              {
                number: '4.1',
                text: 'Services are provided on a self-service basis unless expressly stated otherwise.',
              },
              {
                number: '4.2',
                text: 'Unless expressly purchased, PingCo is not required to provide onboarding, migration, project management, solution design, engineering services, consulting services, service level commitments or implementation services.',
              },
              {
                number: '4.3',
                text: "Any assistance provided by PingCo may be charged at PingCo's then-current standard rates.",
              },
              {
                number: '4.4',
                text: 'Services may be dependent upon Third Party Services and Third Party Vendors.',
              },
            ],
          },
          {
            number: 5,
            title: 'Prepaid Credits & Payments',
            clauses: [
              {
                number: '5.1',
                text: 'To activate and use the Services, the Customer must maintain a Prepaid Credit balance.',
              },
              {
                number: '5.2',
                text: 'An initial minimum Prepaid Credit amount, as published within the Portal from time to time, must be loaded prior to activation of any Service.',
              },
              {
                number: '5.3',
                text: "Fees are deducted from the Customer's Prepaid Credit balance as Services are provisioned, activated or consumed.",
              },
              {
                number: '5.4',
                text: 'Prepaid Credit:',
                items: [
                  'is non-refundable;',
                  'is non-transferable;',
                  'has no cash value; and',
                  'may only be used to acquire Services through the Portal.',
                ],
              },
              {
                number: '5.5',
                text: 'The Customer may elect to enable Auto Top-Up through the Portal.',
              },
              {
                number: '5.6',
                text: "Where Auto Top-Up is enabled, PingCo may charge the Customer's nominated Payment Method when the Prepaid Credit balance falls below the threshold selected by the Customer.",
              },
              {
                number: '5.7',
                text: 'If Auto Top-Up is not enabled, or an Auto Top-Up transaction fails, PingCo may suspend, restrict or cease the provision of Services where the available Prepaid Credit balance reaches zero or is otherwise insufficient to cover applicable Fees.',
                highlight: true,
              },
              {
                number: '5.8',
                text: 'The Customer remains liable for all Fees incurred, including charges that result in a negative balance due to delayed usage records, carrier records, rating events, billing processes or third-party charging events.',
                highlight: true,
              },
              {
                number: '5.9',
                text: 'PingCo may amend Fees from time to time by updating pricing within the Portal.',
              },
              {
                number: '5.10',
                text: 'All Fees are exclusive of taxes unless expressly stated otherwise.',
              },
            ],
          },
          {
            number: 6,
            title: 'Support',
            clauses: [
              {
                number: '6.1',
                text: 'Unless otherwise specified, support for Services acquired through the Portal is limited to email-based support submitted to support@pingco.cloud.',
              },
              {
                number: '6.2',
                text: 'PingCo will use reasonable endeavours to respond to support requests submitted by the Customer, however support provided under these Terms is provided on a best efforts basis only.',
              },
              {
                number: '6.3',
                text: 'PingCo does not provide any Service Levels, target response times, target resolution times, availability commitments or support guarantees in relation to Services acquired under these Terms.',
              },
              {
                number: '6.4',
                text: 'PingCo may prioritise support requests having regard to the nature and severity of the issue, operational requirements and available resources.',
              },
              {
                number: '6.5',
                text: 'Unless expressly purchased, PingCo is not required to provide onboarding, migration, project management, solution design, engineering services, consulting services, implementation services, training or dedicated support resources.',
              },
              {
                number: '6.6',
                text: 'Additional support services, onboarding assistance, project management, engineering services, consulting services, training services and Service Level commitments may be purchased separately from PingCo by contacting sales@pingco.cloud and will be subject to additional Fees and any applicable Service Documents.',
              },
              {
                number: '6.7',
                text: 'To enable PingCo to consider a support request, the Customer must:',
                items: [
                  'submit it via the email in clause 6.1;',
                  'provide sufficient information to identify and, where possible, reproduce the issue (including relevant error messages, logs, screenshots and steps taken);',
                  'nominate and maintain a technical contact; and',
                  'reasonably cooperate with PingCo. PingCo is not required to action requests lacking sufficient information.',
                ],
              },
              {
                number: '6.8',
                text: "Support is provided to the Customer's nominated contact(s) only, and not to end users, customers or any other third party.",
              },
              {
                number: '6.9',
                text: "Support is conditional on the Customer's account being active and in good standing with all Fees paid when due. PingCo is not required to provide support while the Services are suspended or any amount is overdue.",
              },
              {
                number: '6.10',
                text: 'PingCo may change its support processes, channels, hours and resources from time to time, and will use reasonable endeavors to notify the Customer of material changes via the Portal or email.',
              },
            ],
          },
          {
            number: 7,
            title: 'Customer Obligations',
            clauses: [
              {
                number: '7.1',
                text: 'The Customer must:',
                items: [
                  'comply with all applicable laws;',
                  'maintain accurate account information;',
                  'maintain the security of account credentials;',
                  'ensure sufficient Prepaid Credit is maintained;',
                  'comply with all Vendor Terms applicable to the Services;',
                  'ensure all Users comply with these Terms; and',
                  'promptly notify PingCo of any suspected unauthorised access, fraud or security incident.',
                ],
              },
              {
                number: '7.2',
                text: 'The Customer must not:',
                items: [
                  'use the Services for any unlawful purpose;',
                  'engage in fraudulent, deceptive or abusive conduct;',
                  'send spam or unsolicited communications;',
                  'interfere with the operation of the Services; or',
                  'use the Services in a manner that may expose PingCo to legal, regulatory or reputational risk.',
                ],
              },
            ],
          },
          {
            number: 8,
            title: 'Telecommunication Services',
            clauses: [
              {
                number: '8.1',
                text: 'If supplied, telephone numbers, DIDs, porting services and Carrier Services remain subject to carrier, regulatory and third-party approvals.',
              },
              {
                number: '8.2',
                text: 'If submitted, porting dates are estimates only and are not guaranteed.',
              },
              {
                number: '8.3',
                text: 'The Customer does not acquire ownership of any telephone number or numbering resource.',
              },
              {
                number: '8.4',
                text: 'PingCo may recover, reclaim, suspend or reallocate telephone numbers where required by law, regulation, carrier requirements or inactivity policies.',
              },
            ],
          },
          {
            number: 9,
            title: 'Third Party Services',
            clauses: [
              {
                number: '9.1',
                text: 'Certain Services rely on Third Party Services and Third Party Vendors.',
              },
              {
                number: '9.2',
                text: 'PingCo does not control and is not responsible for the availability, performance, functionality or supportability of Third Party Services.',
              },
              {
                number: '9.3',
                text: 'The Customer is responsible for complying with all applicable Vendor Terms.',
              },
            ],
          },
          {
            number: 10,
            title: 'Suspension & Termination',
            clauses: [
              {
                number: '10.1',
                text: 'PingCo may suspend, restrict or terminate access to all or part of the Services where:',
                items: [
                  'insufficient Prepaid Credit is available;',
                  'fraud, misuse or a security incident is suspected;',
                  'required by law, regulation, a carrier or a Third Party Vendor; or',
                  'PingCo reasonably determines that continued provision of the Services may expose PingCo to material legal, regulatory, compliance, operational or reputational risk.',
                ],
              },
              {
                number: '10.2',
                text: 'The Customer may cease using the Services at any time.',
              },
              {
                number: '10.3',
                text: 'PingCo may suspend or terminate the Services immediately where permitted under these Terms.',
              },
              {
                number: '10.4',
                text: 'Upon termination, access to the Services will cease.',
              },
              {
                number: '10.5',
                text: 'Any unused Prepaid Credit is forfeited.',
              },
            ],
          },
          {
            number: 11,
            title: 'Limitations of Liability',
            clauses: [
              {
                number: '11.1',
                text: 'All Intellectual Property Rights in the Services, Portal and related systems remain vested in PingCo or its licensors.',
              },
              {
                number: '11.2',
                text: 'The Customer retains ownership of Customer data.',
              },
              {
                number: '11.3',
                text: 'The Customer grants PingCo a non-exclusive licence to use Customer data to the extent reasonably required to provide the Services.',
              },
              {
                number: '11.4',
                text: 'The Customer is responsible for obtaining all necessary consents and permissions required for its use of the Services.',
              },
            ],
          },
          {
            number: 12,
            title: 'Privacy',
            clauses: [
              {
                number: '12.1',
                text: 'PingCo will process personal information in accordance with its Privacy Policy as updated from time to time.',
              },
            ],
          },
          {
            number: 13,
            title: 'Data',
            clauses: [
              {
                number: '13.1',
                text: "To the maximum extent permitted by law, PingCo's aggregate liability arising out of or in connection with these Terms is limited to the amount of Fees paid by the Customer during the twelve (12) months preceding the event giving rise to the claim.",
              },
              {
                number: '13.2',
                text: 'Neither party is liable for any indirect, consequential, incidental, special, exemplary or punitive loss, including loss of revenue, profit, business opportunity, goodwill or anticipated savings.',
              },
            ],
          },
          {
            number: 14,
            title: 'General',
            clauses: [
              {
                number: '14.1',
                text: 'PingCo may amend these Terms from time to time by publishing an updated version within the Portal.',
              },
              {
                number: '14.2',
                text: 'Continued use of the Services following publication of updated Terms constitutes acceptance of those changes.',
              },
              {
                number: '14.3',
                text: "These Terms constitute the entire agreement governing the Customer's use of the Services.",
              },
              {
                number: '14.4',
                text: 'Marketing material, proposals, demonstrations, statements of work, solution design documents, project plans and other pre-contractual communications do not form part of these Terms unless expressly incorporated by reference.',
              },
              {
                number: '14.5',
                text: 'These Terms are governed by the laws of Victoria, Australia.',
              },
              {
                number: '14.6',
                text: 'The courts of Victoria, Australia have non-exclusive jurisdiction.',
              },
            ],
          },
        ],
      },
      activateButton: 'Activate Wholesaler \u2192',
      toast: {
        activateError: 'Failed to activate wholesaler. Please try again.',
        configError:
          'Your wholesaler was created, but its services, features and carriage could not be saved. Set them from the Services page.',
      },
      infoPanel: {
        title: "You're All Set",
        description:
          'Review your choices and activate instantly. You can adjust everything later from your console.',
        bullets: [
          'Instant activation \u2014 no waiting',
          'Full control from your dashboard',
          'KYC unlocks higher limits when ready',
        ],
      },
    },
    kyc: {
      heading: 'Verify your identity',
      description:
        'Submit your verification application for review. This unlocks higher account limits including longer calls, more concurrent calls, and unrestricted destinations.',
      whatToExpectHeading: 'What to expect',
      whatToExpectBullets: [
        'Your business information is submitted for review',
        'You can continue setup right away',
        "We'll notify you once verification is complete",
      ] as string[],
      verifyButton: KYC_VERIFICATION_COPY.verifyButton,
      launchingButton: KYC_VERIFICATION_COPY.launchingButton,
      pendingHeading: 'Application submitted',
      pendingDescription: KYC_VERIFICATION_COPY.pendingDescription,
      errorMessage: KYC_VERIFICATION_COPY.errorMessage,
    },
    activation: {
      title: 'Request received',
      subtitle: (email: string) =>
        `Your wholesaler is being provisioned in the background. This usually completes within a few minutes. We'll email ${email} as soon as everything is live.`,
      whatHappensNext: {
        heading: 'What happens next',
        bulletMarker: '\u2022',
        bullets: [
          'Your workspace status will show **Pending**, then **Processing**, then **Live**.',
          'You can explore the console in the meantime \u2014 some features unlock once provisioning is complete.',
          'No action required from you until you receive the confirmation email.',
        ],
      },
      continueButton: 'Continue to portal \u2192',
      cnameWarning: {
        title: 'CNAME record not detected',
        viewInstructions: 'View CNAME setup instructions',
        continueAnyway: 'Continue anyway \u2192',
        closeAriaLabel: 'Close CNAME warning dialog',
      },
    },
  },

  // ─── How it works section ─────────────────────────────────────────────────
  howItWorks: {
    eyebrow: 'How it works',
    heading: 'Four steps to live',
    subheading: 'From sign-up to live traffic in minutes \u2014 not weeks.',
    steps: [
      {
        number: '01',
        title: 'Sign up',
        description:
          'Business email, company details, and domain verification. No approvals needed.',
      },
      {
        number: '02',
        title: 'Configure',
        description:
          'Choose your regions, services, and carriage preferences through a guided wizard.',
      },
      {
        number: '03',
        title: 'Add credit',
        description: 'Top up with a minimum prepaid balance. Pay-as-you-go, fully transparent.',
      },
      {
        number: '04',
        title: 'Go live',
        description: 'Your configuration is activated instantly. Start routing calls in minutes.',
      },
    ],
  },

  // ─── Dashboard ────────────────────────────────────────────────────────────
  dashboard: {
    heading: 'Dashboard',
    collapseNav: 'Collapse sidebar',
    expandNav: 'Expand sidebar',
    changeWorkspaceTooltip: 'Switch workspace',
    workspaceSwitcher: {
      title: 'Switch workspace',
      description:
        "Choose a company workspace to manage. Your view will switch to that workspace's settings, services and billing.",
      summaryLabel: 'Workspace',
      ownerLabel: 'Owner',
      avatarFallbackInitials: 'WS',
      logoAlt: (workspaceName: string) => `${workspaceName} logo`,
      currentLabel: 'Current',
      addButton: 'Add Wholesaler',
      closeAriaLabel: 'Close workspace switcher',
      createForm: {
        label: 'New wholesaler company name',
        helper: 'This becomes both the workspace name and the wholesaler profile name.',
        placeholder: 'e.g. Northwind Telecom',
        error: 'Enter at least 2 characters.',
        confirmButton: 'Create & configure',
      },
    },
    overview: {
      heading: 'Welcome back',
      body: "Here's your account overview.",
      warnings: {
        restricted: {
          title: (remaining: string) => `Credit balance critical — ${remaining} remaining`,
          description: 'At or below your restriction level. Services may be restricted imminently.',
          action: 'Top up now',
        },
        credit: {
          title: (remaining: string) => `Credit balance is low — ${remaining} remaining`,
          description: (threshold: string) =>
            `Below your warning threshold of ${threshold}. Consider topping up soon.`,
          action: 'Top up',
        },
        billing: {
          title: 'Complete KYC to unlock higher limits',
          description:
            'Your account is active with starter limits. Verify your identity to remove restrictions.',
          action: 'Start KYC',
        },
      },
      cards: {
        creditBalance: {
          label: 'Credit balance',
          statusRestricted: 'Critical — at or below restriction level',
          statusWarning: 'Low — at or below warning level',
          statusHealthy: 'Healthy — above warning level',
        },
        kyc: {
          label: 'KYC Status',
          pending: 'Pending',
          approved: 'Approved',
          pendingSubtext: 'Starter limits active',
          approvedSubtext: 'Fully verified',
        },
        activeServices: {
          label: 'Active services',
          value: '8',
          subtext: 'Operator Connect, Direct Routing +6',
        },
        activeFeatures: {
          label: 'Active features',
          value: '10',
          subtext: 'TCAP Registration Service, Failover Routing +8',
        },
        teamMembers: {
          label: 'Team members',
        },
      },
      recentActivity: {
        heading: 'Recent activity',
        empty: 'No recent activity yet.',
        columns: {
          activity: 'Activity',
          user: 'User',
          when: 'When',
          action: 'Action',
        },
        showing: (from: string, to: string, total: string) => `Showing ${from}–${to} of ${total}`,
        page: (current: string, total: string) => `Page ${current} of ${total}`,
        markAllRead: 'Mark all as read',
        markingAllRead: 'Marking all as read…',
        // Keyed by `NotificationActionKind` — the label shown when the API supplies no
        // `actionLabel` of its own. One per destination, tab included: the button has to say
        // where it goes, and "View overview" on a link to the carriage tab is how it went wrong.
        actions: {
          switchWorkspace: 'Switch workspace',
          overview: 'View overview',
          services: VIEW_SERVICES_LABEL,
          servicesFeatures: VIEW_FEATURES_LABEL,
          servicesCarriage: VIEW_CARRIAGE_LABEL,
          billing: VIEW_BILLING_LABEL,
          billingCredit: 'View credit',
          billingReceipts: VIEW_RECEIPTS_LABEL,
          kyc: VIEW_KYC_LABEL,
          tcapSettings: 'View TCAP settings',
          team: VIEW_TEAM_LABEL,
          adminSettings: 'View admin settings',
          countryAdmin: 'View country admin',
          serviceAdmin: 'View service admin',
          featureAdmin: 'View feature admin',
          pricingAdmin: 'View pricing admin',
          apiKeysAdmin: 'View API keys',
        },
      },
    },
    navigation: {
      overview: 'Overview',
      setup: 'Initial Setup',
      services: 'Services',
      wholesalerSettings: 'Wholesaler Settings',
      billing: 'Credit & Billing',
      branding: 'Branding',
      team: 'Team',
      numbers: 'Numbers',
      reports: 'Reports',
      settings: 'Settings',
      feedback: 'Feedback',
      adminSettings: 'Admin Settings',
    },
    navGroups: {
      services: 'Services',
      management: 'Management',
      configure: 'Configure',
      setup: 'Setup',
    },
    setup: {
      heading: 'Wholesaler setup',
      body: 'Finish initial platform setup before using dashboard.',
      banner: {
        title: (name: string) => `Creating new wholesaler — ${name}`,
        description: (name: string) => `Cancel to return to ${name} without saving.`,
        fallbackName: 'new workspace',
      },
      steps: {
        company: 'Company',
        regionsCarriage: 'Regions & Carriage',
        services: 'Services',
        features: 'Features',
        branding: 'Branding',
        secureAccount: 'Secure Your Account',
        activate: 'Activate',
      },
      welcomeCard: {
        badge: 'FIRST STEPS',
        heading: (firstName: string) =>
          `Welcome, ${firstName} — let’s create your wholesaler profile`,
        body: (companyName: string) =>
          `Your account for ${companyName} is active, but you haven’t configured a wholesaler workspace yet. Run through the remaining stages to define your service, secure your billing, and go live.`,
        bodyFallback:
          'Your account is active, but you haven’t configured a wholesaler workspace yet. Run through the remaining stages to define your service, secure your billing, and go live.',
        createButton: 'Create wholesaler profile →',
        footerNote:
          'You can return here at any time — the rest of your console will unlock once setup is complete.',
        stages: [
          {
            number: '1',
            label: 'Sign Up',
            description: 'Company details',
          },
          {
            number: '2',
            label: 'Set Up',
            description: 'Regions, services, features, branding',
          },
          {
            number: '3',
            label: 'Clear Up',
            description: 'Secure your account & add credit',
          },
          {
            number: '4',
            label: 'Level Up',
            description: 'Activate and go live',
          },
        ] as { number: string; label: string; description: string }[],
      },
    },
    emptyState: {
      title: 'Coming soon',
      description: 'This dashboard section is scaffolded and ready for implementation.',
    },
    wholesalerSettings: {
      pageTitle: 'Wholesaler Settings',
      pageDescription: 'Verification status, account limits, and TCAP portal configuration.',
      tabs: {
        kyc: 'KYC',
        tcapSettings: 'TCAP Settings',
      },
      toast: {
        tcapSettingsSaved: 'Portal address saved',
        tcapSettingsError: 'Failed to save portal address',
        kycSubmitted: 'Verification application submitted',
        kycSubmitError: 'Failed to submit verification application',
        copyIdError: 'Failed to copy wholesaler ID to clipboard',
      },
      kyc: {
        verifiedHeading: 'Identity verified',
        verifiedDescription: 'Your account has full access. Starter limits have been lifted.',
        unverifiedHeading: 'Verify your identity',
        unverifiedDescription:
          'Submit your verification application to remove starter limits on call duration, concurrency, and destinations.',
        pendingHeading: 'Verification in progress',
        pendingDescription: KYC_VERIFICATION_COPY.pendingDescription,
        verifyButton: KYC_VERIFICATION_COPY.verifyButton,
        launchingButton: KYC_VERIFICATION_COPY.launchingButton,
        errorMessage: KYC_VERIFICATION_COPY.errorMessage,
        retryButton: 'Try Again',
        limitsHeading: 'Starter limits (until verified)',
        limits: [
          '2 minute max call duration',
          '2 concurrent calls',
          '50 calls per month',
          'Domestic non-premium destinations only',
        ] as string[],
        currentLimitsVerifiedHeading: 'Current limits (Verified tier)',
        currentLimitsStarterHeading: 'Current limits (Starter tier)',
        limitsRowMaxCallDuration: 'Max call duration',
        limitsRowConcurrentCalls: 'Concurrent calls',
        limitsRowDestinations: 'Destinations',
        limitsRowDestinationsNote: 'PingCo carriage only',
        limitsRowCallsPerMonth: 'Calls per month',
        verifiedLimitMaxCallDuration: 'Unlimited',
        verifiedLimitConcurrentCalls: 'See trunk capacity',
        verifiedLimitDestinations: 'Domestic and International',
        verifiedLimitCallsPerMonth: 'Unlimited',
        starterLimitMaxCallDuration: '2 minutes',
        starterLimitConcurrentCalls: '2',
        starterLimitDestinations: 'Domestic non-premium only',
        starterLimitCallsPerMonth: '50',
        notYetCompletedHeading: 'KYC not yet completed',
        notYetCompletedDescription: 'Complete identity verification to unlock full capabilities.',
        startVerificationButton: 'Start verification',
        rejectedHeading: 'Verification unsuccessful',
        rejectedDescription: "Stripe couldn't verify your identity documents. Please try again.",
      },
      tcapSettings: {
        heading: 'TCAP settings',
        description:
          'Your branded TCAP Portal address. This is the URL used by the "TCAP Portal" link in the sidebar and shared with your team.',
        wholesalerId: 'Wholesaler ID',
        portalAddress: 'Portal address',
        portalAddressRequired: 'Portal address is required.',
        changeButton: 'Change',
        saveButton: 'Save',
        hostnameSuffix: '.tcap.cloud',
        copiedButton: 'Copied',
      },
    },
    billing: {
      pageTitle: 'Credit and Billing',
      pageDescription: 'Manage credits, top-ups, billing details, and monthly invoices.',
      tabs: {
        credit: 'Credit',
        receipts: 'Receipts',
      },
      receipts: {
        chart: {
          heading: 'Monthly spend by category',
          subheading: 'Stacked charges with total spend overlay',
          platformDidCharge: 'Platform DID Charge',
          pingcoCts: 'PingCo CTS',
          total: 'Total',
        },
        table: {
          emptyLabel: 'No invoices yet.',
          columns: {
            invoice: 'Invoice',
            period: 'Period',
            status: 'Status',
            credits: 'Credits',
            debits: 'Debits',
          },
          statusLabels: {
            current: 'Current',
            paid: 'Paid',
            overdue: 'Overdue',
          },
          totalCharges: 'Total charges',
          initiatedBy: (name: string) => `Added by ${name}`,
          initiatedByLabel: 'Added By',
          initiatedByDetail: (name: string) => name,
          creditSign: '+',
          debitSign: '-',
        },
      },
      balance: {
        heading: 'Account Balance',
        label: 'Available credit',
        addCreditButton: '+ Add credit',
        hideCreditButton: '− Hide',
      },
      addCredit: {
        panelHeading: 'Top up credit',
        selectAmountLabel: 'Select amount',
        amountLabel: 'Amount (USD)',
        amountPlaceholder: '0',
        amountHelperText: 'Enter the credit you want to receive. Fees are added on top.',
        amountLimitsHelperText: (min: string, max: string) =>
          `Enter the credit you want to receive, between ${min} and ${max}. Fees are added on top.`,
        customAmountLabel: 'Or enter a custom amount',
        customAmountPlaceholder: '$0',
        paymentMethodLabel: 'Payment method',
        chargeTo: 'Charge to',
        cardOnFileLabel: (brand: string, last4: string) => `Charge ${brand} •••• ${last4}`,
        cardSelectLabel: (
          brand: string,
          last4: string,
          expMonth: number,
          expYear: number,
          isDefault: boolean,
        ) =>
          `${brand.charAt(0).toUpperCase() + brand.slice(1)} •••• ${last4} (exp ${String(expMonth).padStart(2, '0')}/${String(expYear)})${isDefault ? ' — default' : ''}`,
        useNewCardOption: 'Use a new card...',
        changeCardLabel: 'Use a different card',
        payButton: (amount: string) => `Pay ${amount} USD`,
        successMessage: 'Credit added successfully.',
        errorMessage: 'Failed to add credit. Please try again.',
        noAmountError: 'Please select or enter an amount.',
        noMethodError: 'Please add a payment method first.',
        confirm: {
          heading: 'Confirm payment',
          sections: {
            payment: 'Payment',
            stripeBreakdown: 'Estimated Service fees',
          },
          creditReceivedLabel: 'Credit received',
          totalChargedLabel: 'Total charged',
          cardLabel: 'Card',
          grossLabel: 'Gross charge',
          taxLabel: 'Tax (estimated)',
          feeLabel: 'Processing fee (estimated)',
          netLabel: 'Net (estimated)',
          exchangeRateLabel: 'Exchange rate (estimated)',
          loadingBreakdown: 'Calculating fees…',
          confirmButton: (amount: string) => `Confirm & pay ${amount}`,
        },
        receipt: {
          heading: 'Payment confirmed',
          subheading: (amount: string) => `${amount} has been added to your balance.`,
          sections: {
            payment: 'Payment',
            stripeBreakdown: 'Service charge breakdown',
          },
          creditReceivedLabel: 'Credit received',
          totalChargedLabel: 'Total charged',
          newBalanceLabel: 'New balance',
          cardLabel: 'Card',
          receiptLabel: 'Receipt ID',
          paymentIntentLabel: 'Payment intent',
          grossLabel: 'Gross charge',
          feeLabel: 'Processing fee',
          netLabel: 'Net received',
          exchangeRateLabel: 'Exchange rate',
          downloadButton: 'Download receipt',
        },
      },
      paymentMethod: {
        heading: 'Payment Method',
        noMethodLabel: 'No payment method on file',
        updateButton: 'Update card',
        addButton: 'Add card',
        updateHeading: 'Update payment method',
        nameOnCard: 'Name on card',
        nameOnCardPlaceholder: 'J Smith',
        cardDetails: 'Card details',
        saveButton: 'Save card',
        successMessage: 'Payment method updated.',
        errorMessage: 'Failed to update payment method. Please try again.',
        poweredBy: 'Powered by Stripe',
        devBypass: 'Dev mode: Card details bypassed',
        expiresLabel: 'Expires',
      },
      registeredCards: {
        heading: 'Registered cards',
        privacyNote: 'Details are obfuscated for privacy',
        defaultBadge: 'Default',
        setDefaultButton: 'Set as default',
        removeAriaLabel: 'Remove card',
        noCardsLabel: 'No cards on file',
        removeError: 'Failed to remove card. Please try again.',
        setDefaultError: 'Failed to set default card. Please try again.',
        cardNumberLabel: (brand: string, last4: string) => `${brand} •••• •••• •••• ${last4}`,
      },
      creditThresholds: {
        heading: 'Credit thresholds',
        warningLevel: {
          label: 'Warning level',
          description: 'Alerts when credit falls below this amount',
        },
        restrictionLevel: {
          label: 'Restriction level',
          description: 'Services restricted below this amount',
        },
        suspensionLevel: {
          label: 'Suspension level',
          description: 'Cannot be changed — managed by PingCo',
        },
        autoTopUp: {
          label: 'Auto top-up',
          description: 'Automatically charges your card when balance hits the restriction level',
        },
        topUpAmount: {
          label: 'Top-up amount',
          description: (threshold: string) => `Charged when balance falls below ${threshold}`,
          limitsDescription: (threshold: string, min: string, max: string) =>
            `Charged when balance falls below ${threshold}. Must be between ${min} and ${max}.`,
        },
        saveButton: 'Save thresholds',
        saveSuccess: 'Thresholds saved.',
        saveError: 'Failed to save thresholds. Please try again.',
      },
      invoices: {
        heading: 'Billing History',
        emptyLabel: 'No invoices yet.',
        loadMore: 'Load more',
        columns: {
          date: 'Date',
          description: 'Description',
          amount: 'Amount',
          status: 'Status',
        },
        statusLabels: {
          paid: 'Paid',
          open: 'Open',
          void: 'Void',
          uncollectible: 'Uncollectible',
        },
        downloadButton: 'PDF',
      },
      transactions: {
        heading: 'Billing History',
        emptyLabel: 'No transactions yet.',
        loadMore: 'Load more',
        columns: {
          date: 'Date',
          description: 'Description',
          amount: 'Amount',
          type: 'Type',
          status: 'Status',
        },
        typeLabels: {
          subscription: 'Subscription',
          usage: 'Usage',
          addon: 'Add-on',
          topup: 'Top-up',
          refund: 'Refund',
          adjustment: 'Adjustment',
        },
        statusLabels: {
          completed: 'Completed',
          pending: 'Pending',
          failed: 'Failed',
          refunded: 'Refunded',
        },
        receiptButton: 'Receipt',
      },
      toast: {
        defaultPaymentMethodSaved: 'Default payment method updated',
        defaultPaymentMethodError: 'Failed to update default payment method',
        paymentMethodRemoved: 'Payment method removed',
        paymentMethodRemoveError: 'Failed to remove payment method',
        paymentMethodSaved: 'Payment method saved',
        paymentMethodSaveError: 'Failed to save payment method',
        creditThresholdsSaved: 'Credit thresholds saved',
        creditThresholdsError: 'Failed to save credit thresholds',
        topUpSuccess: 'Credit top-up successful',
        topUpError: 'Failed to top up credit',
      },
    },
  },
  // ─── Team page ───────────────────────────────────────────────────────────
  teamPage: {
    heading: 'Team',
    description: 'Manage your team members and their access roles.',
    inviteButton: '+ Invite member',
    saveSuccess: 'Team roles saved.',
    toast: {
      inviteSent: 'Invite sent',
      inviteError: 'Failed to send invite',
      rolesUpdated: 'Member roles updated',
      rolesError: 'Failed to update member roles',
      memberUpdated: 'Member updated',
      memberUpdateError: 'Failed to update member',
    },
    statusLabels: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    },
    emptyState: 'No team members yet.',
    searchPlaceholder: 'Search by name, email or role…',
    columns: {
      name: 'Name',
      email: 'Email',
      role: 'Role',
      actions: 'Actions',
    },
    noResults: 'No members match your search.',
    editModal: {
      title: 'Edit team member',
      description: (email: string) => `Update name or role for ${email}.`,
      fullNameLabel: 'Full name',
      emailLabel: 'Email',
      emailHint: 'Email cannot be changed. Remove and re-invite to use a different address.',
      roleLabel: 'Role',
      // DEFERRED, not removed: the Security actions row was taken out of the edit-member
      // modal for the passwordless flow. It comes back with authenticator enrollment, along
      // with the reset endpoints in API_ROUTES.
      // securityHeading: 'Security actions',
      // resetPasswordButton: 'Reset password',
      // resetMfaButton: 'Reset MFA',
      saveSuccess: 'Member updated.',
      roleError: 'Role is required.',
    },
    inviteModal: {
      title: 'Invite a team member',
      emailPlaceholder: 'colleague@company.com',
      inviteButton: 'Send invite',
      inviting: 'Inviting…',
      successToast: 'Invitation sent.',
      alreadyMemberToast: 'This person is already on your team.',
      foundToast: (name: string) => `${name} added to your team.`,
    },
  },
  // ─── Feedback modal ───────────────────────────────────────────────────────
  feedbackModal: {
    triggerLabel: 'Feedback',
    title: 'Rate your experience',
    description: 'Help us improve — rate the areas below and add any comments.',
    areaLabels: {
      easeOfUse: 'Ease of use',
      serviceAvailability: 'Service & feature availability',
      pricing: 'Pricing (value and transparency)',
      creditSystem: 'Credit system',
      other: 'Other',
    },
    ratingAriaLabel: (area: string) => `Rate ${area}`,
    commentsLabel: 'Additional comments',
    commentsPlaceholder: "Tell us what's working, what isn't, or what you'd like to see…",
    submitButton: 'Submit feedback',
    submitting: 'Submitting…',
    closeAriaLabel: 'Close feedback form',
    successToast: 'Thanks for your feedback!',
    errorToast: 'Failed to submit feedback.',
  },
  // ─── Account notification content ────────────────────────────────────────
  accountNotifications: {
    credit: {
      added: {
        title: 'Credit added',
        description: (amountUsd: number) =>
          `$${amountUsd.toFixed(2)} USD has been added to your credit balance.`,
        actionLabel: VIEW_BILLING_LABEL,
      },
      warningLevel: {
        title: 'Credit balance low',
        description: (balanceUsd: number) =>
          `Your credit balance has fallen below the warning threshold ($${balanceUsd.toFixed(2)} USD remaining).`,
        actionLabel: TOP_UP_CREDIT_LABEL,
      },
      restrictionLevel: {
        title: 'Credit balance — restriction threshold reached',
        description: (balanceUsd: number) =>
          `Your credit balance has reached the restriction level ($${balanceUsd.toFixed(2)} USD remaining). Some services may be limited.`,
        actionLabel: TOP_UP_CREDIT_LABEL,
      },
      suspensionLevel: {
        title: 'Credit balance critical — suspension imminent',
        description: (balanceUsd: number) =>
          `Your credit balance is critically low ($${balanceUsd.toFixed(2)} USD remaining). Services will be suspended shortly if credit is not restored.`,
        actionLabel: TOP_UP_CREDIT_LABEL,
      },
      autoTopUp: {
        title: 'Auto top-up applied',
        description: (amountUsd: number) =>
          `Your balance was automatically topped up by $${amountUsd.toFixed(2)} USD.`,
        actionLabel: VIEW_BILLING_LABEL,
      },
      paymentMethodAdded: {
        title: 'Payment method added',
        description: 'A new payment method has been saved to your account.',
        actionLabel: VIEW_BILLING_LABEL,
      },
    },
    kyc: {
      completed: {
        title: 'Identity verification complete',
        description:
          'Your KYC verification has been approved. Starter limits have been lifted from your account.',
        actionLabel: VIEW_KYC_LABEL,
      },
    },
    services: {
      servicesChanged: {
        title: 'Services updated',
        description: 'Your service configuration has been updated.',
        actionLabel: VIEW_SERVICES_LABEL,
      },
      featuresChanged: {
        title: 'Features updated',
        description: 'Your feature configuration has been updated.',
        actionLabel: VIEW_FEATURES_LABEL,
      },
      carriageChanged: {
        title: 'Carriage updated',
        description: 'Your carriage preferences have been updated.',
        actionLabel: VIEW_CARRIAGE_LABEL,
      },
      multipleChanged: {
        title: 'Service configuration updated',
        description: 'Multiple sections of your service configuration have been updated.',
        actionLabel: VIEW_SERVICES_LABEL,
      },
    },
    adminSettings: {
      fallbackActor: 'Someone',
      title: (actorName: string, tabLabel: string) => `${actorName} changed ${tabLabel}`,
      description: (changeSummary: string) => changeSummary,
      actionLabel: (tabLabel: string) => tabLabel,
      catalog: {
        addedService: (name: string) => `Added service ${name}.`,
        removedService: (name: string) => `Removed service ${name}.`,
        updatedService: (name: string) => `Updated service ${name}.`,
        updatedServiceGroup: (label: string) => `Updated service group ${label}.`,
        addedFeature: (name: string) => `Added feature ${name}.`,
        removedFeature: (name: string) => `Removed feature ${name}.`,
        updatedFeature: (name: string) => `Updated feature ${name}.`,
        updatedFeatureGroup: (label: string) => `Updated feature group ${label}.`,
        moreChanges: (count: number) => ` ${String(count)} more change${count === 1 ? '' : 's'}.`,
      },
    },
    team: {
      memberAdded: {
        title: 'Team member added',
        description: (email: string) => `${email} has been added to your team.`,
        actionLabel: VIEW_TEAM_LABEL,
      },
      memberRemoved: {
        title: 'Team member removed',
        description: (email: string) => `${email} has been removed from your team.`,
        actionLabel: VIEW_TEAM_LABEL,
      },
    },
    invoices: {
      created: {
        title: 'New invoice available',
        description: (period: string) => `Your invoice for ${period} is now available.`,
        actionLabel: VIEW_RECEIPTS_LABEL,
      },
    },
  },
  // ─── Admin Settings page ─────────────────────────────────────────────────
  adminSettingsPage: {
    pageTitle: 'Admin Settings',
    pageDescription: 'Platform administration and configuration.',
    visibilityOptions: {
      off: 'Off',
      disabled: 'Disabled',
      available: 'Available',
      on: 'On',
    },
    tabs: {
      countryAdmin: 'Country Admin',
      serviceAdmin: 'Service Admin',
      featureAdmin: 'Feature Admin',
      pricingAdmin: 'Pricing Admin',
      ctsFormAdmin: 'CTS Form Admin',
    },
    saved: 'Changes saved.',
    toast: {
      countryConfigSaved: 'Country config saved',
      countryConfigError: 'Failed to save country config',
      servicesConfigSaved: 'Services config saved',
      servicesConfigError: 'Failed to save services config',
      catalogSaved: 'Catalog saved',
      catalogError: 'Failed to save catalog',
      serviceDeleted: 'Service deleted',
      serviceDeleteError: 'Failed to delete service',
      featureDeleted: 'Feature deleted',
      featureDeleteError: 'Failed to delete feature',
      pricingConfigSaved: 'Pricing config saved',
      pricingConfigError: 'Failed to save pricing config',
      currencySaved: 'Currency saved',
      currencyError: 'Failed to save currency',
      ctsFormSaved: 'CTS form saved',
      ctsFormError: 'Failed to save CTS form',
      roleCreated: 'Role added',
      roleCreateError: 'Failed to add role',
      roleUpdated: 'Role updated',
      roleUpdateError: 'Failed to update role',
      roleDeleted: 'Role deleted',
      roleDeleteError: 'Failed to delete role',
      rolePermissionsSaved: 'Role permissions saved',
      rolePermissionsError: 'Failed to save role permissions',
      permissionUpdated: 'Permission updated',
      permissionUpdateError: 'Failed to update permission',
      apiKeyCreated: 'API key created',
      apiKeyCreateError: 'Failed to create API key',
      apiKeyRevoked: 'API key revoked',
      apiKeyRevokeError: 'Failed to revoke API key',
      copyKeyError: 'Failed to copy key to clipboard',
    },
    validation: {
      typeRequired: 'Type is required',
      scopeRequired: 'Scope is required',
      nameRequired: 'Name is required',
    },
    catalog: {
      charCount: (current: number, max: number) => `${String(current)} / ${String(max)}`,
      // Shared by the service and feature delete dialogs — the entity is already named by
      // the title, and the warning itself is identical for both.
      deleteDescription: (name: string) =>
        `This permanently removes ${name} from the catalog for every wholesaler. Hide it instead if you only want to stop offering it.`,
      // The "Tell me more" window renders whatever the editor's Asset information section
      // captured, so it has to read sensibly while those fields are still empty.
      tellMeMore: {
        entityNoun: {
          service: 'service',
          feature: 'feature',
        },
        noAsset: (entity: string) =>
          `No asset has been attached yet. Add one when editing this ${entity} to show a richer walkthrough here.`,
        noLongDescription:
          'No long description yet — add one when editing this item to give customers more context.',
      },
    },
    rolesAdmin: {
      pageTitle: 'Roles',
      pageDescription: 'Manage platform roles and the permissions assigned to each.',
      addRole: '+ Add role',
      columnType: 'Type',
      columnScope: 'Scope',
      columnMembers: 'Members',
      columnPermissions: 'Permissions',
      noRoles: 'No roles yet.',
      deleteTitle: 'Delete role?',
      deleteDescription: (type: string) =>
        `This removes the ${type} role. Members assigned to it will need a new role first.`,
      editor: {
        backLabel: 'Back to roles',
        addTitle: 'Add role',
        addSubtitle: 'New roles start with no permissions assigned.',
        editTitle: 'Edit role',
        editSubtitle: (type: string) => `Update ${type} and its assigned permissions.`,
        typeLabel: 'Type',
        typePlaceholder: 'e.g. support-agent',
        scopeLabel: 'Scope',
        scopePlaceholder: 'e.g. platform',
        permissionsHeading: 'Permissions',
      },
    },
    permissionsAdmin: {
      pageTitle: 'Permissions',
      pageDescription: 'The catalog of permissions that can be assigned to roles.',
      columnKey: 'Key',
      columnName: 'Name',
      columnCategory: 'Category',
      columnDescription: 'Description',
      noPermissions: 'No permissions yet.',
      editModal: {
        title: 'Edit permission',
        nameLabel: 'Name',
        descriptionLabel: 'Description',
      },
    },
    apiKeysAdmin: {
      pageTitle: 'API Keys',
      pageDescription: 'Create and revoke API keys for platform and wholesaler integrations.',
      addApiKey: '+ Create API key',
      columnName: 'Name',
      columnWholesaler: 'Wholesaler',
      columnKeyPrefix: 'Key',
      columnStatus: 'Status',
      columnExpires: 'Expires',
      columnLastUsed: 'Last used',
      noApiKeys: 'No API keys yet.',
      statusActive: 'Active',
      statusRevoked: 'Revoked',
      never: 'Never',
      revokeTitle: 'Revoke API key?',
      revokeDescription: (name: string) =>
        `${name} will stop working immediately. This can't be undone.`,
      revokeButton: 'Revoke',
      createModal: {
        title: 'Create API key',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Zapier integration',
        wholesalerIdLabel: 'Wholesaler ID',
        wholesalerIdPlaceholder: 'Optional — leave blank for a platform-wide key',
        expiresAtLabel: 'Expires',
        expiresAtPlaceholder: 'Optional',
        createButton: 'Create key',
        revealTitle: 'API key created',
        revealDescription:
          "Copy this key now — you won't be able to see it again after closing this dialog.",
        copiedButton: 'Copied',
      },
    },
    countryAdmin: {
      pageTitle: 'Country Administration',
      pageDescription:
        'Configure which carriage options are available per country. Changes affect the signup wizard.',
      searchPlaceholder: 'Search countries across all regions...',
      noResults: 'No countries match your search.',
      columnCountry: 'Country',
      columnByoc: 'BYOC',
      columnOnDemand: 'On Demand',
      columnOnRequest: 'On Request',
      onDemandBadge: 'ON DEMAND',
      onRequestBadge: 'ON REQUEST',
      onDemandDescription:
        'Available for customers to select off the shelf. Numbers can be provisioned immediately. Calling restrictions may apply.',
      onRequestDescription:
        'PingCo can offer this country, but only under a separate agreement. Customers must contact sales before provisioning.',
    },
    serviceAdmin: {
      pageTitle: 'Service Admin',
      pageDescription: 'Configure service availability and visibility.',
      addService: '+ Add service',
      visibleCount: (visible: number, total: number) =>
        `(${String(visible)}/${String(total)} visible)`,
      deleteTitle: 'Delete service?',
      editor: {
        backLabel: 'Back to services',
        addTitle: 'Add service',
        addSubtitle: 'New services are created as Available so customers can opt in.',
        editTitle: 'Edit service',
        editSubtitle: (name: string) => `Update ${name}. Changes apply immediately after saving.`,
        groupInfoTitle: 'Group information',
        groupInfoDescription:
          'Controls how this service appears in lists across the wizard, console and admin.',
        groupLabel: 'Group',
        groupNewPlaceholder: '...or new group',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Webex Contact Center',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'One-line description shown next to the name',
        tcapIdLabel: 'TCAP ID',
        tcapIdPlaceholder: 'e.g. svc_webex_cc',
        tcapIdHelper: 'Identifier used to map this service to the TCAP operational system.',
        assetInfoTitle: 'Asset information',
        assetInfoDescription:
          'Defines what is shown in the "Tell me more" window — the asset preview plus short and long descriptions.',
        assetTypeLabel: 'Asset type',
        assetTypeExternalUrl: 'External URL (new window)',
        assetTypeNone: 'None',
        assetLocationLabel: 'Asset location',
        assetLocationPlaceholder: 'https://example.com/datasheet',
        shortDescriptionLabel: 'Short description',
        shortDescriptionPlaceholder: 'Tagline used in cards and tooltips',
        longDescriptionLabel: 'Long description',
        longDescriptionPlaceholder: 'Detail shown in "Tell me more" modals',
        saveAdd: 'Add service',
      },
    },
    featureAdmin: {
      pageTitle: 'Feature Admin',
      pageDescription: 'Configure feature availability and visibility.',
      addFeature: '+ Add feature',
      visibleCount: (visible: number, total: number) =>
        `(${String(visible)}/${String(total)} visible)`,
      deleteTitle: 'Delete feature?',
      editor: {
        backLabel: 'Back to features',
        addTitle: 'Add feature',
        addSubtitle: 'New features are created as Available so customers can opt in.',
        editTitle: 'Edit feature',
        editSubtitle: (name: string) => `Update ${name}. Changes apply immediately after saving.`,
        groupInfoTitle: 'Group information',
        groupInfoDescription:
          'Controls how this feature appears in lists across the wizard, console and admin.',
        groupLabel: 'Group',
        groupNewPlaceholder: '...or new group',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Smart Routing',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'One-line description shown next to the name',
        tcapIdLabel: 'TCAP ID',
        tcapIdPlaceholder: 'e.g. feat_smart_routing',
        tcapIdHelper: 'Identifier used to map this feature to the TCAP operational system.',
        assetInfoTitle: 'Asset information',
        assetInfoDescription:
          'Defines what is shown in the "Tell me more" window — the asset preview plus short and long descriptions.',
        assetTypeLabel: 'Asset type',
        assetTypeExternalUrl: 'External URL (new window)',
        assetTypeNone: 'None',
        assetLocationLabel: 'Asset location',
        assetLocationPlaceholder: 'https://example.com/datasheet',
        shortDescriptionLabel: 'Short description',
        shortDescriptionPlaceholder: 'Tagline used in cards and tooltips',
        longDescriptionLabel: 'Long description',
        longDescriptionPlaceholder: 'Detail shown in "Tell me more" modals',
        saveAdd: 'Add feature',
      },
    },
    pricingAdmin: {
      pageTitle: 'Pricing Administration',
      pageDescription:
        'Configure platform credit rules, service pricing, and per-country carriage rates. Changes affect the signup wizard and billing.',
      sectionAccountCredit: 'Account & Credit',
      sectionPlatformPricing: 'Platform Pricing',
      sectionServicePricing: 'Service Pricing',
      sectionFeaturePricing: 'Feature Pricing',
      freeCreditAmount: 'Free Credit Amount',
      freeCreditDescription: 'Credit given to new accounts on sign-up',
      currency: 'Currency',
      currencyDescription: 'Billing currency for this wholesaler',
      currencyPlaceholder: 'Select a currency',
      minTopUp: 'Minimum Top-Up',
      minTopUpDescription: 'Minimum additional credit amount',
      maxTopUp: 'Maximum Top-Up',
      maxTopUpDescription: 'Maximum additional credit amount',
      suspensionLevel: 'Suspension Level',
      suspensionLevelDescription: 'Services are suspended at or below this balance',
      suspensionLevelUnsupported:
        'Not yet configurable — the platform does not store this value yet',
      notAvailable: '—',
      // Platform price rows come back named by their price-list enum, not by a display label.
      platformItems: {
        PlatformDid: {
          name: 'Platform DID',
          description: 'Monthly platform charge per provisioned number',
        },
        SbCaaSChannels: {
          name: 'SBCaaS Channels',
          description: 'Monthly charge per SBCaaS channel',
        },
      },
      columnItem: 'Item',
      columnDescription: 'Description',
      columnPricePerMonth: 'Price Per Month',
    },
    ctsFormAdmin: {
      pageTitle: 'CTS Form Admin',
      pageDescription:
        "Configure the documents required on each order's Documents page, per country.",
      countryLabel: 'Country',
      documentsTitle: (countryName: string) => `Documents required for ${countryName}`,
      documentsDescription: "Lowest priority appears at the top of the customer's documents page.",
      addDocument: '+ Add document',
      columnLabel: 'Label',
      columnDescription: 'Description',
      columnOrderType: 'Order type',
      columnPriority: 'Priority',
      emptyDocuments: 'No documents configured for this country.',
      orderTypeOptions: {
        new: 'New',
        port: 'Port',
      },
    },
  },
  // ─── Stripe shared ────────────────────────────────────────────────────────
  stripe: {
    label: 'Payment details',
    nameOnCard: 'Name on card',
    nameOnCardPlaceholder: 'J Smith',
    cardDetails: 'Card details',
    cardLabel: (brand: string, last4: string) => `${brand} •••• ${last4}`,
    poweredBy: 'Powered by Stripe',
  },
  // ─── Stripe charge breakdown (shared across features) ────────────────────
  stripeBreakdown: {
    sectionTitle: 'Service charge breakdown',
    estimateSectionTitle: 'Estimated Service fees',
    phase: {
      estimate: 'Before charge',
      actual: 'After charge',
    },
    fields: {
      chargeAmount: 'Charged amount',
      tax: 'Tax',
      feeTotal: 'Service fee total',
      conversionFee: 'Currency conversion fee',
      net: 'Net after fees',
      exchangeRate: 'Exchange rate',
      localCharge: 'Est. charge in card currency',
      paymentIntent: 'Payment intent',
      card: 'Card',
    },
    estimatedSuffix: '(Estimated)',
    calculatedAtChargeTime: 'Calculated when charge is created',
    loadingFees: 'Calculating fees…',
  },
  // ─── Global errors ────────────────────────────────────────────────────────
  errors: {
    pageError: 'Something went wrong.',
    pageErrorDescription: 'An unexpected error occurred. Try refreshing the page.',
    loadFailed: 'Could not load this page.',
    loadFailedRetry: 'Try again',
    tryRefresh: 'If the problem persists, contact support.',
    retry: 'Refresh page',
    goBack: '← Go back',
    contactPrompt: 'If this keeps happening, email us at',
    contactEmail: 'support@example.com',
    unknown: 'An unexpected error occurred.',
    notFound: {
      heading: '404',
      subheading: 'Page not found',
      body: "The page you're looking for doesn't exist.",
      goHome: 'Go back home',
    },
  },

  // ─── Session timeout ──────────────────────────────────────────────────────
  sessionTimeout: {
    toastTitle: 'Session expired',
    toastDescription: 'You have been signed out after 3 hours. Please sign in again.',
    authExpiredToastDescription: 'Your session could not be renewed. Please sign in again.',
  },
  // ─── Services dashboard page ─────────────────────────────────────────────
  servicesPage: {
    heading: 'Services',
    description: 'Manage services, features, and carriage configuration.',
    toast: {
      configSaved: 'Services config saved',
      configError: 'Failed to save services config',
    },
    tabs: {
      services: 'Services',
      features: 'Features',
      carriage: 'Carriage',
    },
    services: {
      activeCount: (active: number, total: number) => `${String(active)}/${String(total)} active`,
      visibilityOptions: {
        off: 'Off',
        wholesaler: 'Wholesaler',
        partner: 'Partner',
        company: 'Company',
      },
    },
    features: {
      heading: 'Platform Features',
      activeCount: (active: number, total: number) => `${String(active)}/${String(total)} active`,
      platformFee: 'Platform fee',
      featuresTotal: 'Features',
      featuresTotalAmount: (amount: string) => `+${amount}`,
      totalMonthly: 'Total monthly cost',
      didSuffix: DID_MONTH_SUFFIX,
      toggleOptions: {
        off: 'Off',
        on: 'On',
      },
    },
    carriage: {
      preferenceHeading: 'Carriage preference',
      byoc: 'Bring Your Own Carrier',
      pingco: 'PingCo Carriage',
      byocShort: 'BYOC',
      pingcoShort: 'PingCo',
      countryConfigHeading: 'Country Configuration',
      onRequestNotice: 'PingCo On Request countries unlock once KYC is complete.',
    },
  },

  // ─── App Launcher ─────────────────────────────────────────────────────────
  appLauncher: {
    title: 'Admin Center',
    triggerAriaLabel: 'Open app launcher',
    tiles: {
      tcap: 'TCAP',
      wholesalerManagement: 'Wholesaler Management',
      helpCenter: 'Help Center',
    },
    tileTooltips: {
      tcap: 'Open the TCAP portal',
      wholesalerManagement: 'Wholesaler Management — current app',
      helpCenter: 'Open the Help Center',
    },
  },
} as const;
